import React, {Component} from "react";
import {Elements, ElementsConsumer, PaymentElement} from "@stripe/react-stripe-js";
import {Stripe, StripeElements, StripePaymentElementChangeEvent} from "@stripe/stripe-js";
import {PaymentInfo} from "src/modules/flow/payment/model/PaymentInfo";
import {PrePaymentFeeAch} from "src/modules/flow/payment/component/pre-payment/pre-payment-fee-ach/PrePaymentFeeAch";
import {PrePaymentAgreementAch} from "../../pre-payment-agreement/PrePaymentAgreementAch";
import {PrePaymentAchSubmitButton} from "./submit/PrePaymentAchSubmitButton";
import {SnackbarServiceSingleton, StripeServiceSingleton} from "src/Context";
import "../../PrePayment.scss";
import {PaymentForm} from "src/modules/flow/payment/model/PaymentForm";

interface Props {
    paymentInfo: PaymentInfo,
    fee: string,
    disabled: boolean,
}

interface State {
    clientSecret?: string;
    loadingIntent: boolean;
    formValidity: boolean;
    paymentElementComplete: boolean;
}

export class PrePaymentAch extends Component<Props, State> {

    private readonly form = React.createRef<HTMLFormElement>()
    private readonly stripeService = StripeServiceSingleton;
    private readonly snackbarService = SnackbarServiceSingleton;

    constructor(p: Props) {
        super(p);
        this.state = {
            loadingIntent: true,
            formValidity: false,
            paymentElementComplete: false
        };
        this.stripeService.initStripe(p.paymentInfo.stripePublicKey);
    }

    componentDidMount() {
        const preloadedSecret = this.props.paymentInfo.achPaymentIntentClientSecret;
        if (preloadedSecret) {
            this.setState({
                clientSecret: preloadedSecret,
                loadingIntent: false
            });
            return;
        }
        this.createPaymentIntent();
    }

    private createPaymentIntent = () => {
        this.setState({loadingIntent: true});
        this.stripeService.fetchAchPaymentIntentSecret({
            agreedToTerms: true,
            userAgent: window.navigator.userAgent
        }).then(secret => {
            this.setState({
                clientSecret: secret,
                loadingIntent: false
            });
        }).catch(err => {
            const message = typeof err === 'string' ? err : 'Unable to start ACH payment.';
            console.log(err);
            this.snackbarService.error(message);
            this.setState({loadingIntent: false});
        });
    };

    private handleFormChange = () => {
        const form = this.form.current as PaymentForm | null;
        const validity = form?.checkValidity() ?? false;
        this.setState({
            formValidity: validity
        });
    };

    private handlePaymentElementChange = (event: StripePaymentElementChangeEvent) => {
        this.setState({
            paymentElementComplete: event.complete
        });
    };

    private isSubmitDisabled = (stripe: Stripe | null, elements: StripeElements | null): boolean => {
        return this.state.loadingIntent
            || this.props.disabled
            || !stripe
            || !elements
            || !this.state.formValidity;
    };

    private paymentElementReturnUrl = (): string => {
        return this.props.paymentInfo.achReturnUrl || window.location.href;
    };

    render() {
        if (this.state.loadingIntent && !this.state.clientSecret) {
            return <div className="field">Loading payment form...</div>;
        }

        if (!this.state.clientSecret) {
            return <div className="field error_message">Unable to load payment form. Please try again.</div>;
        }

        const appearance = {
            theme: 'stripe' as const,
            variables: {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                colorPrimary: '#1976d2',
                colorText: '#000000',
                colorDanger: '#d32f2f'
            }
        };

        return (
            <Elements stripe={this.stripeService.stripePromise()} options={{
                clientSecret: this.state.clientSecret,
                appearance: appearance,
                loader: 'auto'
            }}>
                <ElementsConsumer>
                    {({stripe, elements}) => (
                        <PrePaymentAchForm
                            fee={this.props.fee}
                            formRef={this.form}
                            onFormChange={this.handleFormChange}
                            onPaymentElementChange={this.handlePaymentElementChange}
                            submitButtonDisabled={this.isSubmitDisabled(stripe, elements)}
                            stripe={stripe}
                            elements={elements}
                            clientSecret={this.state.clientSecret}
                            returnUrl={this.paymentElementReturnUrl()}
                            disabled={this.props.disabled || this.state.loadingIntent}
                            paymentElementComplete={this.state.paymentElementComplete}
                        />
                    )}
                </ElementsConsumer>
            </Elements>
        );
    }
}

interface PrePaymentAchFormProps {
    fee: string;
    formRef: React.RefObject<HTMLFormElement>;
    onFormChange: () => void;
    onPaymentElementChange: (event: StripePaymentElementChangeEvent) => void;
    submitButtonDisabled: boolean;
    stripe: Stripe | null;
    elements: StripeElements | null;
    clientSecret?: string;
    returnUrl: string;
    disabled: boolean;
    paymentElementComplete: boolean;
}

class PrePaymentAchForm extends Component<PrePaymentAchFormProps> {

    private handlePaymentElementChange = (event: StripePaymentElementChangeEvent) => {
        this.props.onPaymentElementChange(event);
    };

    render() {
        return <>
            <form ref={this.props.formRef} onChange={this.props.onFormChange}>
                <fieldset disabled={this.props.disabled}>
                    <PrePaymentFeeAch fee={this.props.fee}/>
                    <div className="field">
                        <PaymentElement onChange={this.handlePaymentElementChange}/>
                    </div>
                    <PrePaymentAgreementAch/>
                    <PrePaymentAchSubmitButton submitButtonDisabled={this.props.submitButtonDisabled}
                                               stripe={this.props.stripe}
                                               elements={this.props.elements}
                                               clientSecret={this.props.clientSecret}
                                               returnUrl={this.props.returnUrl}
                                               paymentElementComplete={this.props.paymentElementComplete}
                    />
                </fieldset>
            </form>
        </>
    }
}

